import {
  useOrganizationsServiceOrganizationsAccessList,
  useUsersServiceUsersInviteCreate
} from '@organization/api/queries'
import { TeamList, TeamModify } from '@organization/api/requests'
import { inviteUrl } from '@organization/utils'
import { useSessionContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { App, Button, Form, Input, Modal } from 'antd'
import Select, { DefaultOptionType } from 'antd/es/select'
import { ArrowDown } from 'iconsax-react'

import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { OptionNotFound } from '../OptionNotFound'
import { TeamPermissions } from './TeamPermissions'
import { TeamSelect } from './TeamSelect'

type Props = {
  title: string
  team?: TeamList | null
  okText: string
  hideTitle?: boolean
  isLoading?: boolean
  parent?: number | null
  onSubmit: (payload: Partial<TeamModify>) => void
  onCancel: () => void
}

export const TeamForm = ({
  title,
  okText,
  team,
  hideTitle,
  isLoading,
  parent,
  onSubmit,
  onCancel
}: Props) => {
  const [form] = Form.useForm()
  const { notification } = App.useApp()
  const { user } = useSessionContext()
  const { t } = useTranslation()

  const [searchValue, setSearchValue] = useState('')

  const organizationId = Number(user?.organization?.id)

  const { data } = useOrganizationsServiceOrganizationsAccessList(
    {
      organization: organizationId,
      search: searchValue
    },
    [organizationId, searchValue],
    {
      enabled: !!user?.organization?.id
    }
  )

  const { mutate: inviteUser, isPending: isInviting } =
    useUsersServiceUsersInviteCreate({
      onSuccess: () => {
        notification.success({
          message: t('users.email_sent')
        })
        onCancel()
      },
      onError: (e) => showApiErrors(e, form)
    })

  const organizationUserList = data?.results?.map((user) => ({
    value: user?.user?.id,
    label: user?.user?.email
  }))

  const onInvite = () => {
    if (!organizationId || !searchValue) return

    inviteUser({
      requestBody: {
        organization_id: Number(organizationId),
        emails: [searchValue],
        invite_url: inviteUrl
      }
    })
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      const normalizedUsers = values?.users
        ?.map((user: DefaultOptionType) => {
          if (typeof user === 'number') {
            return { id: user, roles: values.roles }
          }

          if (user?.value) {
            return { id: user?.value, roles: values.roles }
          }

          return null
        })
        .filter(
          (u: DefaultOptionType): u is { id: string | number } => u !== null
        )

      const payload: Partial<TeamModify> = {
        organization: Number(organizationId),
        name: values.name,
        ...((!team || hideTitle) && {
          new_users: normalizedUsers
        }),
        parent: parent || values.parent,
        application_permissions: values?.application_permissions
      }

      onSubmit(payload)

      notification.success({
        message: t('users.team_updated')
      })

      onCancel()
    } catch (err) {
      showApiErrors(err)
    }
  }

  useEffect(() => {
    if (team) {
      form.setFieldsValue({
        name: team.name,
        application_permissions: team.application_permissions
      })
    }

    if (parent) {
      form.setFieldValue('parent', parent)
    }
  }, [team, parent, form])

  return (
    <Modal
      title={title}
      open
      destroyOnHidden
      onCancel={onCancel}
      footer={[
        <Button key="1" onClick={onCancel}>
          {t('button.cancel')}
        </Button>,
        <Button
          key="2"
          type="primary"
          htmlType="submit"
          loading={isLoading || isInviting}
          onClick={handleSubmit}
        >
          {okText}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        {!hideTitle && (
          <Form.Item
            name="name"
            label={t('form.item.team_name')}
            rules={[{ required: true, message: t('form.message.team_name') }]}
          >
            <Input placeholder={t('form.placeholder.team_name')} />
          </Form.Item>
        )}

        {(!team || hideTitle) && (
          <Form.Item
            label={t('table.users')}
            name="users"
            rules={[{ required: true }]}
          >
            <Select
              mode="multiple"
              placeholder={t('form.placeholder.search_email_or_name')}
              filterOption={false}
              onSearch={setSearchValue}
              onSelect={() => setSearchValue('')}
              onBlur={() => setSearchValue('')}
              onClear={() => setSearchValue('')}
              menuItemSelectedIcon={<ArrowDown />}
              options={organizationUserList}
              notFoundContent={
                <OptionNotFound
                  isLoading={isInviting}
                  keyword={searchValue}
                  onInvite={onInvite}
                />
              }
            />
          </Form.Item>
        )}

        <TeamPermissions />

        {parent && (
          <Form.Item label={t('common.team')} name="parent">
            <TeamSelect parent={String(parent)} disabled />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
